import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
  import interactiveMasterplanApi from "@/services/api/interactiveMasterplanApi";

  interface Point {
    x: number;
    y: number;
  }

  interface LabelMeta {
    offsetX: number;
    offsetY: number;
    scale: number;
    rotation: number;
    logoUrl?: string;
    logoFileName?: string;
    labelText?: string;
    boundaryOpacity?: number;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'EstateBoundaryDrawer',
  props: {
    svgFile: {},
    estateTitle: {},
    estateId: {},
    userId: {},
    initialBoundaryPath: {},
    initialLabelMetadata: {}
  },
  emits: ["complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emit = __emit;

  const svgContainer = ref<HTMLDivElement | null>(null);
  const svgOverlay = ref<SVGSVGElement | null>(null);
  const svgContent = ref("");
  const overlayViewBox = ref("0 0 1000 1000");

  const zoomLevel = ref(1);
  const panX = ref(0);
  const panY = ref(0);
  const isDragging = ref(false);
  const wasDragging = ref(false);
  const dragStart = ref({ x: 0, y: 0 });
  const mouseDownPos = ref({ x: 0, y: 0 });

  const isDrawing = ref(false);
  const draftPoints = ref<Point[]>([]);
  const mouseViewPos = ref<Point>({ x: 0, y: 0 });
  const draggingPointIndex = ref(-1);
  const wasPointDragging = ref(false);
  const boundaryPoints = ref<Point[]>([]);
  const stashedBoundary = ref<Point[] | null>(null);
  const estateLabelAdjustments = ref<LabelMeta>({
    offsetX: 0,
    offsetY: 0,
    scale: 3,
    rotation: 0,
    boundaryOpacity: 0.55,
  });

  const logoUrl = ref<string>("");
  const logoFileName = ref<string>("");
  const labelText = ref<string>("");
  const uploadingLogo = ref(false);
  const logoFileInput = ref<HTMLInputElement | null>(null);

  const logoWidth = computed(() => {
    const parts = overlayViewBox.value.split(" ").map(Number);

    return Math.round(Math.min(parts[2] || 1000, parts[3] || 1000) / 40) * 25;
  });

  const logoHeight = computed(() => Math.round(logoWidth.value * 0.4));

  const pathToPoints = (path: string): Point[] => {
    if (!path) return [];

    const tokens = path.trim().split(/\s+/);
    const points: Point[] = [];

    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i] === "M" || tokens[i] === "L") {
        const coords = tokens[i + 1]?.split(",");

        if (coords?.length === 2) {
          points.push({ x: parseFloat(coords[0]), y: parseFloat(coords[1]) });
          i++;
        }
      }
    }

    return points;
  };

  const pointsToPath = (points: Point[]): string => {
    if (points.length === 0) return "";

    return (
      `M ${points[0].x},${points[0].y} ` +
      points
        .slice(1)
        .map((p) => `L ${p.x},${p.y}`)
        .join(" ") +
      " Z"
    );
  };

  (() => {
    if (props.initialBoundaryPath) {
      boundaryPoints.value = pathToPoints(props.initialBoundaryPath);
    }

    const initialMeta = props.initialLabelMetadata;
    const initialEstateTitle = props.estateTitle;

    labelText.value = initialMeta?.labelText ?? initialEstateTitle;

    if (initialMeta) {
      estateLabelAdjustments.value = {
        ...estateLabelAdjustments.value,
        offsetX: initialMeta.offsetX,
        offsetY: initialMeta.offsetY,
        scale: initialMeta.scale,
        rotation: initialMeta.rotation,
        ...(initialMeta.boundaryOpacity != null
          ? { boundaryOpacity: initialMeta.boundaryOpacity }
          : {}),
      };

      if (initialMeta.logoUrl) logoUrl.value = initialMeta.logoUrl;

      if (initialMeta.logoFileName)
        logoFileName.value = initialMeta.logoFileName;
    }
  })();

  const hasBoundary = computed(() => boundaryPoints.value.length >= 3);

  const boundaryCentroid = computed<Point | null>(() => {
    if (boundaryPoints.value.length < 3) return null;

    const pts = boundaryPoints.value;

    return {
      x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
      y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
    };
  });

  const labelFontSize = computed(() => {
    const parts = overlayViewBox.value.split(" ").map(Number);

    return Math.round(Math.min(parts[2] || 1000, parts[3] || 1000) / 40) * 2;
  });

  const estateLabelTextRef = ref<SVGTextElement | null>(null);
  const estateLabelBbox = ref<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const estateLabelPadX = computed(() => labelFontSize.value * 0.1);
  const estateLabelPadY = computed(() => labelFontSize.value * 0.05);
  const estateLabelPillRx = computed(() => labelFontSize.value * 0.08);

  const measureEstateLabelBbox = async () => {
    await nextTick();

    if (estateLabelTextRef.value?.isConnected) {
      const bb = estateLabelTextRef.value.getBBox();

      estateLabelBbox.value = {
        x: bb.x,
        y: bb.y,
        width: bb.width,
        height: bb.height,
      };
    }
  };

  const estateLabelGroupTransform = computed(() => {
    if (!boundaryCentroid.value) return "";

    const cx = boundaryCentroid.value.x + estateLabelAdjustments.value.offsetX;
    const cy = boundaryCentroid.value.y + estateLabelAdjustments.value.offsetY;
    const { scale, rotation } = estateLabelAdjustments.value;

    if (scale === 1 && rotation === 0) return "";

    const parts = [`translate(${cx} ${cy})`];

    if (scale !== 1) parts.push(`scale(${scale})`);

    if (rotation !== 0) parts.push(`rotate(${rotation})`);

    parts.push(`translate(${-cx} ${-cy})`);
    return parts.join(" ");
  });

  const svgStyle = computed(() => ({
    transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoomLevel.value})`,
    transformOrigin: "0 0",
  }));

  const getSvgCoordsFromEvent = (e: MouseEvent): Point | null => {
    if (!svgOverlay.value) return null;

    const pt = svgOverlay.value.createSVGPoint();

    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svgOverlay.value.getScreenCTM();

    if (!ctm) return null;

    const svgPt = pt.matrixTransform(ctm.inverse());

    return { x: svgPt.x, y: svgPt.y };
  };

  const startDrawing = () => {
    stashedBoundary.value = boundaryPoints.value.length
      ? [...boundaryPoints.value]
      : null;
    draftPoints.value = boundaryPoints.value.length
      ? [...boundaryPoints.value]
      : [];
    boundaryPoints.value = [];
    isDrawing.value = true;
  };

  const finishDrawing = () => {
    if (!isDrawing.value || draftPoints.value.length < 3) return;

    boundaryPoints.value = [...draftPoints.value];
    stashedBoundary.value = null;
    isDrawing.value = false;
    draftPoints.value = [];
  };

  const cancelDrawing = () => {
    if (stashedBoundary.value) {
      boundaryPoints.value = stashedBoundary.value;
      stashedBoundary.value = null;
    }

    isDrawing.value = false;
    draftPoints.value = [];
  };

  const clearBoundary = () => {
    boundaryPoints.value = [];
  };

  const applyZoom = (newZoom: number, anchorX: number, anchorY: number) => {
    const ratio = newZoom / zoomLevel.value;

    panX.value = anchorX - (anchorX - panX.value) * ratio;
    panY.value = anchorY - (anchorY - panY.value) * ratio;
    zoomLevel.value = newZoom;
  };

  const handleZoomIn = () => {
    const c = svgContainer.value;

    applyZoom(
      Math.min(zoomLevel.value * 1.2, 20),
      c ? c.clientWidth / 2 : 0,
      c ? c.clientHeight / 2 : 0
    );
  };
  const handleZoomOut = () => {
    const c = svgContainer.value;

    applyZoom(
      Math.max(zoomLevel.value / 1.2, 0.1),
      c ? c.clientWidth / 2 : 0,
      c ? c.clientHeight / 2 : 0
    );
  };
  const handleResetView = () => {
    zoomLevel.value = 1;
    panX.value = 0;
    panY.value = 0;
  };

  const handleWheel = (e: WheelEvent) => {
    const c = svgContainer.value;

    if (!c) return;

    const rect = c.getBoundingClientRect();

    applyZoom(
      Math.max(0.1, Math.min(20, zoomLevel.value * (e.deltaY > 0 ? 0.9 : 1.1))),
      e.clientX - rect.left,
      e.clientY - rect.top
    );
  };

  const handleMouseDown = (e: MouseEvent) => {
    wasDragging.value = false;
    mouseDownPos.value = { x: e.clientX, y: e.clientY };

    if (e.button === 0) {
      isDragging.value = true;
      dragStart.value = {
        x: e.clientX - panX.value,
        y: e.clientY - panY.value,
      };
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (draggingPointIndex.value >= 0) {
      const coords = getSvgCoordsFromEvent(e);

      if (coords) {
        const updated = [...draftPoints.value];

        updated[draggingPointIndex.value] = coords;
        draftPoints.value = updated;
      }

      return;
    }

    if (isDragging.value) {
      const dx = e.clientX - mouseDownPos.value.x;
      const dy = e.clientY - mouseDownPos.value.y;

      if (Math.sqrt(dx * dx + dy * dy) > 4) wasDragging.value = true;

      if (wasDragging.value) {
        panX.value = e.clientX - dragStart.value.x;
        panY.value = e.clientY - dragStart.value.y;
      }
    }

    if (isDrawing.value) {
      const coords = getSvgCoordsFromEvent(e);

      if (coords) mouseViewPos.value = coords;
    }
  };

  const handleMouseUp = () => {
    if (draggingPointIndex.value >= 0) {
      draggingPointIndex.value = -1;
      wasPointDragging.value = true;
      return;
    }

    isDragging.value = false;
  };

  const handleContainerClick = (e: MouseEvent) => {
    if (!isDrawing.value || wasDragging.value) return;

    if (wasPointDragging.value) {
      wasPointDragging.value = false;
      return;
    }

    const coords = getSvgCoordsFromEvent(e);

    if (coords) draftPoints.value.push(coords);
  };

  const handleDoubleClick = () => {
    if (isDrawing.value) finishDrawing();
  };

  const handleContextMenu = () => {
    if (isDrawing.value) draftPoints.value.pop();
  };

  const handlePointMouseDown = (e: MouseEvent, index: number) => {
    if (!isDrawing.value) return;

    draggingPointIndex.value = index;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isDrawing.value) return;

    const target = e.target as HTMLElement;

    if (
      ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
      target.isContentEditable
    )
      return;

    if (
      e.key === "Backspace" ||
      ((e.key === "z" || e.key === "Z") && (e.ctrlKey || e.metaKey))
    ) {
      e.preventDefault();
      draftPoints.value.pop();
    }
  };

  const handleComplete = () => {
    const trimmedLabelText = labelText.value.trim();

    emit("complete", {
      boundaryPath: hasBoundary.value
        ? pointsToPath(boundaryPoints.value)
        : null,
      estateLabelMetadata: {
        ...estateLabelAdjustments.value,
        ...(trimmedLabelText ? { labelText: trimmedLabelText } : {}),
        ...(logoUrl.value
          ? { logoUrl: logoUrl.value, logoFileName: logoFileName.value }
          : {}),
      },
    });
  };

  const triggerLogoUpload = () => {
    logoFileInput.value?.click();
  };

  const handleLogoUpload = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    uploadingLogo.value = true;

    try {
      const formData = new FormData();

      formData.append("file", file);
      formData.append("userId", props.userId);
      formData.append("estateId", props.estateId);

      const response = await interactiveMasterplanApi.uploadEstateLabelLogo(
        formData
      );
      const data = response.data as { url: string; fileName: string };

      logoUrl.value = data.url;
      logoFileName.value = data.fileName;
    } catch (err) {
      console.error("Failed to upload estate label logo:", err);
      alert("Failed to upload logo. Please try again.");
    } finally {
      uploadingLogo.value = false;
      input.value = "";
    }
  };

  const removeLogo = async () => {
    logoUrl.value = "";
    logoFileName.value = "";
    await nextTick();
    await measureEstateLabelBbox();
  };

  const extractViewBox = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent.value, "image/svg+xml");
    const svg = doc.querySelector("svg");

    if (svg) {
      const viewBox = svg.getAttribute("viewBox");

      if (viewBox) {
        overlayViewBox.value = viewBox;
      } else {
        const width = svg.getAttribute("width") || "1000";
        const height = svg.getAttribute("height") || "1000";

        overlayViewBox.value = `0 0 ${width} ${height}`;
      }
    }

    setTimeout(measureEstateLabelBbox, 50);
  };

  onMounted(() => {
    const reader = new FileReader();

    reader.onload = (e) => {
      svgContent.value = e.target?.result as string;
      setTimeout(extractViewBox, 100);
    };
    reader.readAsText(props.svgFile);
    window.addEventListener("keydown", handleKeyDown);
  });

  watch(
    () => estateLabelAdjustments.value,
    () => {
      measureEstateLabelBbox();
    },
    { deep: true }
  );
  watch(
    boundaryPoints,
    () => {
      measureEstateLabelBbox();
    },
    { deep: true }
  );
  watch(labelText, async () => {
    await nextTick();
    await measureEstateLabelBbox();
  });
  watch(logoUrl, async () => {
    await nextTick();
    await measureEstateLabelBbox();
  });

  onUnmounted(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });

const __returned__ = { props, emit, svgContainer, svgOverlay, svgContent, overlayViewBox, zoomLevel, panX, panY, isDragging, wasDragging, dragStart, mouseDownPos, isDrawing, draftPoints, mouseViewPos, draggingPointIndex, wasPointDragging, boundaryPoints, stashedBoundary, estateLabelAdjustments, logoUrl, logoFileName, labelText, uploadingLogo, logoFileInput, logoWidth, logoHeight, pathToPoints, pointsToPath, hasBoundary, boundaryCentroid, labelFontSize, estateLabelTextRef, estateLabelBbox, estateLabelPadX, estateLabelPadY, estateLabelPillRx, measureEstateLabelBbox, estateLabelGroupTransform, svgStyle, getSvgCoordsFromEvent, startDrawing, finishDrawing, cancelDrawing, clearBoundary, applyZoom, handleZoomIn, handleZoomOut, handleResetView, handleWheel, handleMouseDown, handleMouseMove, handleMouseUp, handleContainerClick, handleDoubleClick, handleContextMenu, handlePointMouseDown, handleKeyDown, handleComplete, triggerLogoUpload, handleLogoUpload, removeLogo, extractViewBox, ref, computed, onMounted, onUnmounted, nextTick, watch, get interactiveMasterplanApi() { return interactiveMasterplanApi } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})